import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import { FaCheckCircle, FaEdit, FaFilter, FaPlus, FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import { formatPrice } from "../../helpers/utility";
import {
  CBL_PAGE,
  CPMT_INVOICES_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_FORM_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  CPMT_TRANSFER_PAGE,
  PAY_PAGE,
  RETOUR_PAGE,
} from "../../routes/routeConstants";

import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";

import "./pos-reception.css";
import "./BonComptoireList.css";

const CReceptionPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const { receiptNotes = [], isLoading } = useSelector((state) => state.receiptNote);
  const {
    role,
    businessName,
    fullName,
    commercialId,
    pointOfSaleId,
  } = useSelector((state) => state.auth);

  const isAdmin = role === "ROLE_ADMIN";
  const [dateModal, setDateModal] = useState(false);
  const [activeTab, setActiveTab] = useState("RECEPTION");
  const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);

  const now = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  const formatDate = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const [filters, setFilters] = useState({
    dateFrom: formatDate(now),
    dateTo: formatDate(now),
    query: "",
  });

  useEffect(() => {
    dispatch(receiptNoteActions.search(filters));
  }, [filters.dateFrom, filters.dateTo]);

  const searchList = (nextFilters = filters) => {
    dispatch(receiptNoteActions.search(nextFilters));
  };

  const clearFilters = () => {
    const nextFilters = {
      dateFrom: formatDate(now),
      dateTo: formatDate(now),
      query: "",
    };
    setFilters(nextFilters);
    searchList(nextFilters);
  };

  const openSearchKeypad = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSearchKeypadOpen(true);
  };

  const handleValidate = (note) => {
    Swal.fire({
      title: t("common.confirm"),
      text: t("reception.confirmValidateMessage"),
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          receiptNoteActions.validateReceiptNote({
            id: note.id,
            onSuccess: () => {
              Swal.fire(t("common.saved"), "", "success");
              searchList();
            },
          })
        );
      }
    });
  };

  const getStatusBadge = (status) => {
    if (status === "VALIDE") return <Badge color="success">Validee</Badge>;
    return <Badge color="secondary">En attente</Badge>;
  };

  const totalTTC = receiptNotes.reduce(
    (sum, note) => sum + Number(note?.montantTotalTTC || 0),
    0
  );
  const validatedCount = receiptNotes.filter((note) => note?.statut === "VALIDE").length;

  return (
    <Container fluid className="pos-page pos-list-page">
      <POSHeader
        businessName={businessName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        isAdmin={isAdmin}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "INVOICES") navigate(CPMT_INVOICES_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
      />

      <div className="pos-topbar shadow-sm">
        <div className="pos-search">
          <FaSearch className="icon" />
          <Input
            innerRef={searchInputRef}
            type="text"
            value={filters.query}
            readOnly
            inputMode="none"
            onKeyDown={(e) => {
              e.preventDefault();
            }}
            placeholder="Recherche reception..."
            className="pos-search-input pos-nokeyboard"
            onFocus={(e) => {
              e.target.blur();
              openSearchKeypad();
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              openSearchKeypad();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              openSearchKeypad();
            }}
            onClick={openSearchKeypad}
          />
        </div>

        <div className="pos-actions">
          <div className="pos-date-range">
            <FormGroup className="mb-0 pos-date-field">
              <Label className="pos-label">{t("common.from", "Du")}</Label>
              <Input
                type="date"
                className="form-control pos-input"
                value={filters.dateFrom || ""}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, dateFrom: e.target.value || null }))
                }
              />
            </FormGroup>

            <FormGroup className="mb-0 pos-date-field">
              <Label className="pos-label">{t("common.to", "Au")}</Label>
              <Input
                type="date"
                className="form-control pos-input"
                value={filters.dateTo || ""}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, dateTo: e.target.value || null }))
                }
              />
            </FormGroup>
          </div>

          <div className="pos-action-stack">
            <Label className="pos-label pos-label-spacer">.</Label>
            <div className="pos-btn pos-total-chip">
              <span>Total </span>
              <strong>{formatPrice(totalTTC)}</strong>
            </div>
          </div>

          <div className="pos-action-stack">
            <Label className="pos-label pos-label-spacer">.</Label>
            <Button
              color="primary"
              className="pos-btn pos-btn-primary"
              onClick={() => navigate(CPMT_RECEPTION_FORM_PAGE)}
            >
              <FaPlus />
              <span className="d-none d-md-inline ms-2">Nouvelle</span>
            </Button>
          </div>
        </div>
      </div>

      <Card className="pos-list-card">
        <CardBody className="p-0">
          {isLoading ? (
            <div className="pos-loading p-4 text-center">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t("common.loading")}</div>
            </div>
          ) : (
            <div className="pos-list">
              {receiptNotes.map((note) => (
                <div key={note.id} className="pos-list-row">
                  <div className="left">
                    <div className="top">
                      <strong className="numero">{note.numero || "-"}</strong>
                      {getStatusBadge(note.statut)}
                    </div>

                    <div className="meta">
                      <span>{note.dateReception || "-"}</span>
                      <span>{note.fournisseurNom || "-"}</span>
                    </div>

                    <div className="amounts">
                      <span className="ttc">{formatPrice(note.montantTotalTTC)}</span>
                    </div>
                  </div>

                  <div className="right">
                    <Button
                      color="warning"
                      className="pos-icon-btn pos-edit"
                      onClick={() => {
                        navigate(CPMT_RECEPTION_FORM_PAGE, {
                          state: { reception: note },
                        });
                      }}
                    >
                      <FaEdit />
                    </Button>

                    {note.statut === "EN_ATTENTE" && (
                      <Button
                        color="success"
                        className="pos-icon-btn pos-confirm"
                        onClick={() => handleValidate(note)}
                      >
                        <FaCheckCircle />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {!receiptNotes.length && (
                <div className="p-4 text-center text-muted">Aucune reception trouvee</div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <POSKeypadModal
        isOpen={searchKeypadOpen}
        toggle={() => setSearchKeypadOpen(false)}
        value={filters.query}
        onChange={(val) => setFilters((prev) => ({ ...prev, query: val }))}
        onSearch={() => searchList({ ...filters, query: filters.query })}
        title="Recherche Reception"
      />

      <Modal isOpen={dateModal} toggle={() => setDateModal(false)} centered>
        <ModalHeader toggle={() => setDateModal(false)}>Filtrer par date</ModalHeader>

        <ModalBody>
          <Row className="g-3">
            <Col md={6}>
              <label className="fw-bold">Date debut</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </Col>

            <Col md={6}>
              <label className="fw-bold">Date fin</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </Col>
          </Row>
        </ModalBody>

        <div className="p-3 d-flex justify-content-end gap-2">
          <Button color="secondary" onClick={() => setDateModal(false)}>
            Annuler
          </Button>

          <Button
            color="primary"
            onClick={() => {
              searchList();
              setDateModal(false);
            }}
          >
            Appliquer
          </Button>
        </div>
      </Modal>
    </Container>
  );
};

export default CReceptionPage;
